
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { generateOrderCode, loadWidget } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import {
  required,
  numeric,
  min,
  max,
  min_value,
} from 'vee-validate/dist/rules';
import { Getter, namespace } from 'vuex-class';
import {
  IAccountHolderDetails,
  ICreditBalance,
  IInvoice,
  IKairosKashPaymentResponseData,
  IPayments,
  IPaymentStatusIndicatorResponse,
} from '@/types/types';
import { Watch } from 'vue-property-decorator';
import { initialPaymentStatus } from '@/store/modules/payments';

// Register validation rules
extend('required', {
  ...required,
  message: 'This field is required',
});
extend('numeric', {
  ...numeric,
  message: 'This field must contain only numbers',
});
extend('min', {
  ...min,
  message: 'This field must be at least {length} characters',
});
extend('max', {
  ...max,
  message: 'This field cannot be longer than {length} characters',
});
extend('min_value', {
  ...min_value,
  message: 'This field must be {min} or greater',
});
const authModule = namespace('auth');
const paymentModule = namespace('payments');

@Component({
  name: 'MomoPayment',
  components: {
    ValidationProvider,
    ValidationObserver,
    InternetConnection: loadWidget('pages/InternetConnection'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
  },
})
export default class MomoPayment extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: ICreditBalance;
  @authModule.Getter('getOtpSentStatus') otpSentStatus!: string;
  @paymentModule.Getter('getCurrentPaymentStep') currentPaymentStep!: string;
  @paymentModule.Getter('getAccountHolderDetails')
  walletDetails!: IAccountHolderDetails;
  @paymentModule.Getter('getPaymentStatusIndicatorResponse')
  paymentStatusIndicatorResponse!: IPaymentStatusIndicatorResponse;
  @paymentModule.Getter('getPaymentResponse')
  paymentResponse!: IKairosKashPaymentResponseData;
  @authModule.Getter('GET_CURRENT_RESEND_TIMER') currentResentTimer!: number;

  // Wizard step tracking
  currentStep = 1;

  // Payment state
  payment = {
    amount: null as number | null,
    recipientMsisdn: '',
  };
  /** set when arriving from Billing to settle a specific invoice */
  invoice: IInvoice | null = null;
  otpCode = '';
  reference = '';

  // Available networks
  networks = [
    { name: 'MTN Mobile Money', code: 'MTN' },
    { name: 'Vodafone Cash', code: 'VODAFONE' },
    { name: 'AirtelTigo Money', code: 'AIRTELTIGO' },
  ];

  getNetworkLogo(code: string): string {
    // Use static images from the public directory
    const logos: Record<string, string> = {
      MTN: '/img/networks/mtn.png',
      VODAFONE: '/img/networks/vodafone.jpg',
      AIRTELTIGO: '/img/networks/airteltigo.jpg',
    };

    return logos[code] || '';
  }

  async validateAndMoveToNextStep(): Promise<void> {
    const observer = this.$refs.detailsForm as InstanceType<
      typeof ValidationObserver
    >;
    const isValid = await observer.validate();
    if (isValid) {
      if (this.currentPaymentStep === 'DETAILS') {
        await this.$store.dispatch(
          'payments/accountHolderCheck',
          `233${this.payment.recipientMsisdn.slice(-9)}`
        );
      } else if (this.currentPaymentStep === 'VERIFY_ACCOUNT') {
        //verify account
        await this.$store.dispatch('auth/verifyOTPCode', {
          code: this.otpCode,
        });
      } else if (this.currentPaymentStep === 'MAKE_PAYMENT') {
        await this.$store.dispatch('payments/initiatePayment', {
          amount: Number(this.payment.amount),
          recipientMsisdn: this.payment.recipientMsisdn,
          payer: this.walletDetails.accountName ?? this.walletDetails.msisdn,
          narration: this.invoice
            ? `INV ${this.invoice.invoiceNumber}`
            : `CREDIT_PURCHASE`,
          reference: this.reference,
          network: this.walletDetails.network,
          /* settles the invoice: the server takes the amount from it and
             links the payment, so the document cannot be underpaid */
          ...(this.invoice ? { invoiceId: this.invoice.id } : {}),
        });
      } else {
        // complete payment
      }
    }
  }

  @Watch('currentPaymentStep')
  onCurrentPaymentStepChanged(step: string): void {
    switch (step) {
      case 'DETAILS':
        return;
      case 'VERIFY_ACCOUNT':
        this.$store.dispatch('auth/sendOTP', {
          recipient: `233${this.payment.recipientMsisdn.slice(-9)}`,
        });
        this.currentStep = 2;
        return;
      case 'MAKE_PAYMENT':
        this.currentStep = 3;
        return;
      case 'PAYMENT_CONFIRMATION': {
        this.currentStep = 4;
        return;
      }
    }
  }
  /**
   * Arriving as ?invoice=<id>&msisdn=<number> from Billing: the invoice fixes
   * the amount and seeds the payer number, so the wizard opens ready to go.
   */
  private async loadInvoiceFromQuery(): Promise<void> {
    const id = Number(this.$route.query.invoice);
    if (!id) {
      return;
    }
    const invoice = await this.$store.dispatch('invoices/details', id);
    if (!invoice) {
      return;
    }
    this.invoice = invoice;
    this.payment.amount = Number(invoice.amount);
    this.payment.recipientMsisdn =
      (this.$route.query.msisdn as string) || invoice.recipientMsisdn || '';
  }

  created(): void {
    this.reference = `${generateOrderCode()}${Date.now()}`.slice(0, 20);
    this.loadInvoiceFromQuery();
    this.$store.commit('payments/UPDATE_CURRENT_PAYMENT_STEP', 'DETAILS');
    this.$store.commit(
      'payments/UPDATE_PAYMENT_RESPONSE',
      initialPaymentStatus
    );
    this.$store.commit('payments/UPDATE_PAYMENT_DETAILS', {
      status: '',
      reference: '',
      transactionId: '',
    });
  }
}
